<div class="mx-auto w-full max-w-3xl px-4 py-3">
  <div class="mb-3 flex items-center gap-2">
    <button type="button" uiButton variant="outline" size="sm" (click)="goBack()">Back</button>
    <h1 class="m-0 font-heading text-lg font-semibold text-fg">New bulk disbursement</h1>
  </div>

  <p class="mb-4 text-sm text-muted">
    Pick the merchant, then its application, wallet and provider. Load a spreadsheet or CSV with the columns
    <code>destination_account</code> and <code>amount</code> (optional <code>destination_name</code>,
    <code>merchant_reference</code>, <code>purpose</code>). The payload is encrypted before upload. If one line is wrong,
    nothing is sent.
  </p>

  @if (error()) {
    <ui-alert variant="danger">{{ error() }}</ui-alert>
  }
  @if (success(); as done) {
    <ui-alert variant="success">
      Batch created ({{ done.reference }}). It is processed in the background.
      <a class="font-semibold text-brand underline" [routerLink]="batchLink()">Follow its progress</a>
    </ui-alert>
  }

  <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
    <ui-form-field class="md:col-span-2" label="Merchant">
      @if (merchantId) {
        <div class="flex items-center gap-2">
          <strong class="text-fg">{{ merchantName || merchantId }}</strong>
          <button type="button" uiButton variant="ghost" size="sm" (click)="clearMerchant()">Change</button>
        </div>
      } @else {
        <lib-autocomplete
          listPath="auth/merchants/"
          [minLength]="1"
          placeholder="Search a merchant…"
          (itemSelect)="selectMerchant($event)"
        />
      }
    </ui-form-field>

    @if (merchantId) {
      <ui-form-field label="Batch name">
        <input uiInput [(ngModel)]="batchName" name="bn" />
      </ui-form-field>
      <ui-form-field label="Application">
        <select uiInput [(ngModel)]="applicationId" name="app">
          <option value="">Choose…</option>
          @for (app of applications(); track app.id) {
            <option [value]="app.id">{{ app.name }} ({{ app.environment }})</option>
          }
        </select>
      </ui-form-field>
      <ui-form-field label="Wallet to debit">
        <select uiInput [ngModel]="walletSelected()" (ngModelChange)="chooseWallet($event)" name="wal">
          <option value="">Choose…</option>
          @for (w of wallets(); track w.id) {
            <option [value]="w.id">{{ w.country_code }} · {{ w.currency_code }} (balance {{ w.balance }})</option>
          }
        </select>
      </ui-form-field>
      <ui-form-field label="Provider">
        <ui-autocomplete
          name="pc" placeholder="Search a provider…" ariaLabel="Provider"
          [options]="providerOptions()" [loading]="providersLoading()" [filterLocal]="false"
          [disabled]="!walletSelected()"
          [(ngModel)]="providerCode"
          (search)="providerSearch$.next($event.trim())"
        />
      </ui-form-field>
      <ui-form-field class="md:col-span-2" label="Webhook URL for the payouts (optional)">
        <input uiInput [(ngModel)]="webhookUrl" name="wh" />
      </ui-form-field>
      <ui-form-field class="md:col-span-2" label="File (.xlsx, .xls or .csv)">
        <input type="file" uiInput accept=".xlsx,.xls,.csv" (change)="onFileSelected($event)" />
        @if (limits(); as lim) {
          <p class="mb-0 mt-1 text-xs text-muted">Up to {{ lim.bulk_effective_max_lines }} lines for this merchant.</p>
        }
      </ui-form-field>
    }
  </div>

  @if (lines().length) {
    <ui-card class="mt-3" title="Double-check">
      <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        <ui-form-field label="Total amount of the file">
          <input uiInput [(ngModel)]="declaredTotal" name="dt" inputmode="decimal" placeholder="0.00" />
        </ui-form-field>
        <ui-form-field label="Number of lines">
          <input uiInput [(ngModel)]="declaredCount" name="dc" inputmode="numeric" placeholder="0" />
        </ui-form-field>
      </div>
      <ul class="m-0 mt-1 list-none p-0 text-sm text-fg">
        <li [class.text-success]="totalMatches()" [class.text-danger]="declaredTotal !== '' && !totalMatches()">
          {{ totalMatches() ? '✔' : '•' }} The file adds up to {{ total() | number: '1.2-2' }}
          {{ wallet()?.currency_code }}
        </li>
        <li [class.text-success]="countMatches()" [class.text-danger]="declaredCount !== '' && !countMatches()">
          {{ countMatches() ? '✔' : '•' }} The file has {{ lines().length }} lines
        </li>
        <li [class.text-success]="withinLimit()" [class.text-danger]="!withinLimit()">
          {{ withinLimit() ? '✔' : '✖' }} Within the line limit
        </li>
        <li [class.text-success]="problemCount() === 0" [class.text-danger]="problemCount() > 0">
          {{ problemCount() === 0 ? '✔' : '✖' }} Every line has an account and a valid amount
          @if (problemCount() > 0) {
            ({{ problemCount() }} to fix)
          }
        </li>
      </ul>
    </ui-card>

    <div uiTableWrap class="mt-3">
      <table uiTable>
        <thead>
          <tr>
            <th>#</th>
            <th>Account</th>
            <th>Name</th>
            <th class="text-right">Amount</th>
            <th>Reference</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (line of lines().slice(0, 100); track $index) {
            <tr [class]="problems()[$index].length ? 'bg-danger-bg' : ''">
              <td>{{ $index + 1 }}</td>
              <td class="break-words">{{ line.destination_account }}</td>
              <td>{{ line.destination_name }}</td>
              <td class="text-right">{{ line.amount }}</td>
              <td class="break-words">{{ line.merchant_reference }}</td>
              <td class="text-xs text-danger">{{ problems()[$index].join(' · ') }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    @if (lines().length > 100) {
      <p class="mt-1 text-xs text-muted">First 100 lines shown; the whole file is checked on send.</p>
    }
  }

  @if (issues().length) {
    <div uiTableWrap class="mt-3">
      <table uiTable>
        <thead>
          <tr>
            <th>Row</th>
            <th>Field</th>
            <th>Problem</th>
          </tr>
        </thead>
        <tbody>
          @for (issue of issues(); track $index) {
            <tr>
              <td>{{ issue.row ?? '—' }}</td>
              <td>{{ issue.field || '—' }}</td>
              <td>{{ issue.message }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  <div class="mt-3">
    <button type="button" uiButton [disabled]="loading() || !canSubmit()" (click)="submit()">
      @if (loading()) {
        Submitting…
      } @else {
        Encrypt &amp; send {{ lines().length || '' }} payouts
      }
    </button>
  </div>
</div>
